import React, { useState } from "react";
import { FiWifi, FiWifiOff } from "react-icons/fi";
import { AqiBadge, aqiMeta, co2Color, pm25Color } from "./aqmHelpers";

const RoomTile = ({ room, selected, onClick }) => {
  const m = aqiMeta(room.aqi);
  const isSelected = selected === room.id;
  return (
    <div
      className={`aqm-room-tile ${isSelected ? "aqm-room-sel" : ""}`}
      style={{ borderTopColor: m.color, cursor: "pointer" }}
      onClick={() => onClick(room.id === selected ? null : room.id)}
    >
      <div className="aqm-rt-header">
        <div className="aqm-rt-name">{room.name}</div>
        {room.status === "Online"
          ? <FiWifi size={12} color="#10b981" />
          : <FiWifiOff size={12} color="#ef4444" />
        }
      </div>
      <div className="aqm-rt-aqi" style={{ color: m.color }}>{room.aqi}</div>
      <AqiBadge aqi={room.aqi} />
      <div className="aqm-rt-params">
        <span style={{ color: co2Color(room.co2) }}>CO₂ {room.co2}</span>
        <span style={{ color: pm25Color(room.pm25) }}>PM2.5 {room.pm25}</span>
      </div>
      <div className="aqm-rt-thum">
        <span>🌡 {room.temp}°C</span>
        <span>💧 {room.hum}%</span>
      </div>
    </div>
  );
};

const RoomDetail = ({ room, onClose }) => {
  const m = aqiMeta(room.aqi);
  return (
    <div className="aqm-room-detail">
      <div className="aqm-rd-header" style={{ borderColor: m.color }}>
        <div>
          <div className="aqm-rd-name">{room.name}</div>
          <div className="aqm-rd-id">{room.deviceId} · {room.status === "Online" ? "🟢 Online" : "🔴 Offline"} · {room.lastSeen}</div>
        </div>
        <button className="aqm-rd-close" onClick={onClose}>✕</button>
      </div>

      <div className="aqm-rd-aqi-big">
        <div style={{ color: m.color, fontSize: 48, fontWeight: 800, lineHeight: 1 }}>{room.aqi}</div>
        <div>
          <AqiBadge aqi={room.aqi} />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Air Quality Index</div>
        </div>
      </div>

      <div className="aqm-rd-params">
        {[
          { label: "CO₂",         value: `${room.co2} ppm`,     color: co2Color(room.co2),    warn: room.co2 > 1000 ? "Increase ventilation" : null },
          { label: "TVOC",        value: `${room.tvoc} mg/m³`,  color: room.tvoc > 0.5 ? "#ef4444" : "#10b981" },
          { label: "PM2.5",       value: `${room.pm25} µg/m³`,  color: pm25Color(room.pm25) },
          { label: "PM10",        value: `${room.pm10} µg/m³`,  color: pm25Color(room.pm10 / 2) },
          { label: "Temperature", value: `${room.temp}°C`,       color: "#f97316" },
          { label: "Humidity",    value: `${room.hum}%`,         color: "#3b82f6" },
          { label: "Noise",       value: `${room.noise} dB`,     color: room.noise > 70 ? "#f59e0b" : "#10b981" },
        ].map(p => (
          <div key={p.label} className="aqm-rd-param">
            <div className="aqm-rdp-val" style={{ color: p.color }}>{p.value}</div>
            <div className="aqm-rdp-lbl">{p.label}</div>
            {p.warn && <div style={{ fontSize: 9, color: "#ef4444", marginTop: 1 }}>⚠ {p.warn}</div>}
          </div>
        ))}
      </div>

      {room.co2 > 1000 && (
        <div className="aqm-rd-ai">
          🤖 AI: CO₂ at {room.co2} ppm — {room.co2 > 1200 ? "CRITICAL. Evacuate or open all windows immediately." : "Increase fresh air ventilation. Recommend reducing occupancy by 30%."}
        </div>
      )}
    </div>
  );
};

const AqmFloorMapPanel = ({ d }) => {
  const [activeFloor, setActiveFloor] = useState(d.floors[0].id);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const floor = d.floors.find(f => f.id === activeFloor);
  const selRoom = selectedRoom ? floor?.rooms.find(r => r.id === selectedRoom) : null;

  return (
    <div className="aqm-floormap-panel">

      {/* Floor selector */}
      <div className="aqm-floor-tabs">
        {d.floors.map(fl => {
          const fm = aqiMeta(fl.overallAqi);
          return (
            <button
              key={fl.id}
              className={`aqm-floor-tab ${activeFloor === fl.id ? "aqm-floor-tab-active" : ""}`}
              onClick={() => { setActiveFloor(fl.id); setSelectedRoom(null); }}
              style={activeFloor === fl.id ? { borderColor: fm.color, color: fm.color, background: fm.bg } : {}}
            >
              <span className="aqm-floor-tab-lbl">{fl.label}</span>
              <span style={{ fontWeight: 700, color: fm.color }}>{fl.overallAqi}</span>
              <AqiBadge aqi={fl.overallAqi} />
            </button>
          );
        })}
      </div>

      <div className="aqm-floormap-body">
        {/* Room grid */}
        <div className="aqm-room-grid">
          <div className="aqm-section-title">{floor?.name} — {floor?.rooms.length} Rooms</div>
          <div className="aqm-rooms-list">
            {floor?.rooms.map(room => (
              <RoomTile key={room.id} room={room} selected={selectedRoom} onClick={setSelectedRoom} />
            ))}
          </div>
          {/* AQI Legend */}
          <div className="aqm-legend-row">
            {[
              [50, "Good"], [100, "Moderate"], [150, "Sensitive"], [200, "Unhealthy"], [300, "Very Unhealthy"]
            ].map(([v, l]) => {
              const lm = aqiMeta(v);
              return <span key={v} className="aqm-legend-chip" style={{ background: lm.bg, color: lm.text }}>{l}</span>;
            })}
          </div>
        </div>

        {/* Room detail */}
        {selRoom ? (
          <RoomDetail room={selRoom} onClose={() => setSelectedRoom(null)} />
        ) : (
          <div className="aqm-room-no-sel">
            <div style={{ fontSize: 40 }}>🏢</div>
            <div className="aqm-rns-title">Select a room</div>
            <div className="aqm-rns-sub">Click any room tile to view detailed air quality data</div>
            <div style={{ marginTop: 14 }}>
              <div className="aqm-floor-summary-grid">
                {floor?.rooms.map(r => {
                  const rm = aqiMeta(r.aqi);
                  return (
                    <div key={r.id} className="aqm-fsg-chip"
                      style={{ background: rm.bg, borderColor: rm.color, color: rm.text }}
                      onClick={() => setSelectedRoom(r.id)}>
                      {r.name}: {r.aqi}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="aqm-ai-strip">
        🤖 AI: {floor?.rooms.filter(r => r.co2 > 1000).length > 0
          ? `${floor.rooms.filter(r => r.co2 > 1000).length} rooms on ${floor.name} have CO₂ above 1000 ppm. Zone ventilation increase recommended.`
          : `All rooms on ${floor?.name} within acceptable CO₂ range.`
        }
      </div>
    </div>
  );
};

export default AqmFloorMapPanel;
